import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { TranslateService } from '@ngx-translate/core';
import { RouterBaseService } from './router/router-public-api';
import type { StateObject } from './router/router-public-api';
import type { PostBootstrapInit } from './post-bootstrap-init';

export interface PageTitleServiceConfiguration {
  applicationTitle: string;
}

export const PAGE_TITLE_SERVICE_CONFIGURATION = new InjectionToken<PageTitleServiceConfiguration>('pageTitleServiceConfiguration');

/**
 * Routes can modify the document title :
 * 1-Entering the route state name as a translation key at the locale files,
 * Example:
 * {
    "page.home.summary": "Home Page",
    "tdWb": {
      "home": {
        "KEY": "Lorem ipsum ..."
    ...
 *
 * 2-Entering the title translate key on the route state definition object.
 * Example
 * {
      name: 'page.components.test',
      url: '/test',
      Component: TestComponent,
      data: {
        title: 'tdWb.component.TITLE'
      }
    },
 *
 */
@Injectable({
  providedIn: 'root'
})
export class PageTitleService implements PostBootstrapInit  {

  constructor(@Inject(RouterBaseService) private routerService: RouterBaseService,
              @Inject(Title) private titleService: Title,
              @Inject(TranslateService) private translateService: TranslateService,
              @Optional()
              @Inject(PAGE_TITLE_SERVICE_CONFIGURATION) private configuration?: PageTitleServiceConfiguration) { }

  init(): void {
    this.routerService.setPageTitle((transitionTo: StateObject): void => {
      this.handleStateTransition(transitionTo);
    });
  }

  getParentStateName(stateName: string): string {
    // determine depth of state tree
    const numberOfPeriods = stateName.match(/\./g);

    // stop climbing state tree when we reach true parent state ie:  page.<state>
    if (numberOfPeriods !== undefined && numberOfPeriods !== null && numberOfPeriods.length === 1) {
      // if there is no translation for the parent state, then return null to exit loop
      if (this.translateService.instant(stateName) === stateName) {
        return null;
      }
      return stateName;
    }

    const index = stateName.lastIndexOf('.');

    if (index > 0) {
      return stateName.substring(0, index);
    }

    return null;
  }

  getTranslation(stateName: string): string {
    let title;
    let key = stateName;
    do {
      title = this.translateService.instant(key);
      if (key !== title) {
        return title;
      }
      key = this.getParentStateName(key);
    } while (key !== null);

    return null;
  }

  handleStateTransition(transitionTo: StateObject): void {
    // Add extra code from page title to get translation
    const toState = transitionTo;
    let pageTitle: string;

    if (!toState.abstract) {
      const data = toState?.data as  { title: string };
      pageTitle = data?.title?.length > 0 ?
        this.getTitleByTranslateKey(data.title) :
        this.getTitleByStateName(toState.name);
      if (pageTitle !== null) {
        this.titleService.setTitle(pageTitle);
      }
    }
  }

  getTitleByTranslateKey(translateKey: string): string {
    return this.appendApplicationName(this.translateService.instant(translateKey));
  }

  getTitleByStateName(stateName: string): string {
    return this.appendApplicationName(this.getTranslation(stateName));
  }

  private appendApplicationName(pageTitle: string): string {
    if (pageTitle !== null) {
      if (this.configuration?.applicationTitle !== undefined) {
        pageTitle = `${this.configuration.applicationTitle} - ${pageTitle}`;
      }
      return pageTitle;
    }
  }
}
